// api/app/UnitDetail.js
import React, { useCallback, useEffect, useState, useRef } from 'react'; // Ajout de useRef
import { useLocation, useParams } from 'react-router-dom';
import { UnitService, AuthService } from '../services/api';
import ExpectedChildren from './ExpectedChildren';
import { forgetLocal } from '../services/expectedListStorage';
import { startupLink, accessLabel, isUnitStarted } from '../services/teamAccess';
import { useNavigate } from 'react-router-dom';
import { unitTypeLabels } from '../constants/unitTypes'; // Importer les constantes
import { QRCodeCanvas } from 'qrcode.react'; // Importer pour le QR Code
import { FaTable, FaBuilding, FaArrowRightFromBracket, FaChevronDown, FaCalendar, FaCopy, FaTrash } from 'react-icons/fa6';
import logo from '../assets/logo.png';
import './Dashboard.css'; // Réutiliser les styles du layout
import './UnitDetail.css';

const TABS = [
    ['animator', 'QR de démarrage (AS / direction)'],
    ['parents', "Lien d'invitation pour les parents"],
    ['expected', 'Liste des participants attendus'],
    ['devices', "Membres de l'équipe"],
];

const UnitDetail = () => {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const location = useLocation();
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const [unit, setUnit] = useState(null);
    const [devices, setDevices] = useState([]);
    const [name, setName] = useState('');
    const [type, setType] = useState('');
    const [start_dt, setStartDt] = useState('');
    const [end_dt, setEndDt] = useState('');
    const [initial_start_dt, setInitialStartDt] = useState('');
    const [copySuccess, setCopySuccess] = useState(''); // Pour le message de confirmation de copie
    const [parentCopySuccess, setParentCopySuccess] = useState('');
    // Onglet gardé dans le hash (#parents…) : survit au rechargement et se partage par lien.
    const hashTab = location.hash.slice(1);
    const tab = TABS.some(([key]) => key === hashTab) ? hashTab : TABS[0][0];
    const [startup, setStartup] = useState(null); // { init_token, expires_at }
    const [startupError, setStartupError] = useState('');

    // Le formulaire n'est rempli qu'au premier chargement : un rafraîchissement
    // ne doit pas écraser une saisie en cours.
    const fetchUnitDetails = useCallback(async (initial) => {
        try {
            if (initial) setLoading(true);

            const data = await UnitService.getStructureInfo();
            const unitData = data.units.find(u => u.id === id);
            setError('');

            if (unitData) {
                setUnit(unitData);
                if (initial) {
                    setName(unitData.name);
                    setType(unitData.type);
                    setStartDt(unitData.start_dt.split('T')[0]);
                    setEndDt(unitData.end_dt.split('T')[0]);
                    setInitialStartDt(unitData.start_dt.split('T')[0]);
                }
            }

            const devicesResponse = await UnitService.getUnitDevices(id);
            setDevices(devicesResponse);
        } catch (err) {
            console.error('Error fetching unit details:', err);
            if (initial) setError('Impossible de charger les détails du groupe.');
        }
        finally {
            if (initial) setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchUnitDetails(true);
    }, [fetchUnitDetails]);

    // Ex. : le QR de démarrage vient d'être scanné, l'onglet parents doit le savoir sans recharger la page.
    const handleTabChange = (key) => {
        if (key === tab) return;
        navigate({ hash: key }, { replace: true });
        fetchUnitDetails(false);
    };

    const handleNameChange = (e) => {
        setName(e.target.value);
    };

    const handleTypeChange = (e) => {
        setType(e.target.value);
    };

    const handleSave = async () => {
        try {
            await UnitService.updateUnit({ id, name, type, start_dt, end_dt });
            navigate('/dashboard');
        } catch (error) {
            alert('Une erreur est survenue', error);
        }
    };

    const handleDateChange = (e) => {
        const { name, value } = e.target;
        if (name === 'start_dt') {
            setStartDt(value);
            if (new Date(value) > new Date(end_dt)) {
                setEndDt('');
            }
        } else {
            setEndDt(value);
        }
    };

    const handleDelete = async () => {
        const confirmation = window.confirm("ATTENTION ! Cela empêchera l'équipe et les parents d'accéder aux données");
        if (confirmation) {
            try {
                await UnitService.deleteUnit(id);
                forgetLocal(id);
                navigate('/dashboard');
            } catch (error) {
                alert('Une erreur est survenue lors de la suppression', error);
            }
        }
    };

    const publicWebsite = window._env_?.PUBLIC_WEBSITE || '';
    const shareableLink = startup ? startupLink(publicWebsite, id, startup.init_token) : '';
    const parentShareableLink = `${publicWebsite}/join_organism_as_parent?orga_id=${id}`;
    const qrCodeRef = useRef(null);
    const parentQrCodeRef = useRef(null);

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareableLink);
            setCopySuccess('Lien copié dans le presse-papiers !');
            setTimeout(() => setCopySuccess(''), 2000);
        } catch (err) {
            console.error('Erreur lors de la copie du lien: ', err);
            setCopySuccess('Erreur lors de la copie.');
            setTimeout(() => setCopySuccess(''), 2000);
        }
    };

    const handleDownloadQRCode = (ref, filename) => {
        const canvas = ref.current?.querySelector('canvas');
        if (canvas) {
            const pngUrl = canvas
                .toDataURL("image/png")
                .replace("image/png", "image/octet-stream");
            let downloadLink = document.createElement("a");
            downloadLink.href = pngUrl;
            downloadLink.download = filename;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
        } else {
            console.error("Impossible de trouver le canvas du QR Code pour le téléchargement.");
        }
    };

    // Chaque génération invalide le QR précédent : un QR qui a circulé ne sert plus.
    const handleCreateStartup = async () => {
        try {
            setStartupError('');
            setStartup(await UnitService.createInitToken(id));
        } catch (err) {
            console.error('Erreur lors de la création du QR de démarrage: ', err);
            setStartupError(err.response?.status === 409
                ? "Ce séjour a déjà été démarré. Rechargez la page."
                : 'Impossible de créer le QR de démarrage.');
        }
    };

    const handleCopyParentLink = async () => {
        try {
            await navigator.clipboard.writeText(parentShareableLink);
            setParentCopySuccess('Lien copié dans le presse-papiers !');
            setTimeout(() => setParentCopySuccess(''), 2000);
        } catch (err) {
            console.error('Erreur lors de la copie du lien parent: ', err);
            setParentCopySuccess('Erreur lors de la copie.');
            setTimeout(() => setParentCopySuccess(''), 2000);
        }
    };

    const handleDeleteDevice = async (deviceId) => {
        const confirmation = window.confirm("Voulez-vous vraiment supprimer l'accès de ce membre de l'équipe ?");
        if (confirmation) {
            try {
                await UnitService.deleteEnrollment(id, deviceId);
                // Mettre à jour la liste des appareils après suppression
                const devicesResponse = await UnitService.getUnitDevices(id);
                setDevices(devicesResponse);
            } catch (error) {
                alert('Une erreur est survenue lors de la suppression de l\'appareil', error);
            }
        }
    };

    if (loading) {
        return <div className="loading-container">Chargement...</div>;
    }

    if (error) {
        return <div className="loading-container">{error}</div>;
    }

    // Démarré = un premier membre a rejoint le séjour et en détient la clé.
    const hasAnimators = isUnitStarted(unit);

    let maxStartDate = '';
    if (initial_start_dt) {
        const initialStartDate = new Date(initial_start_dt);
        let year = initialStartDate.getFullYear();
        const august31 = new Date(year, 7, 31);

        if (initialStartDate > august31) {
            year += 1;
        }
        maxStartDate = `${year}-08-31`;
    }

    let maxEndDate = '';
    if (start_dt) {
        const startDate = new Date(start_dt);
        let year = startDate.getFullYear();
        const august31 = new Date(year, 7, 31);

        if (startDate > august31) {
            year += 1;
        }
        maxEndDate = `${year}-08-31`;
    }

    return (
        <div id="dashboard-container">
            <div id="dashboard-layout">
                <aside id="sidebar" className={sidebarOpen ? 'open' : ''}>
                    <div id="sidebar-header">
                        <h1><img src={logo} alt="Fiche Santé Logo" style={{ height: '30px', marginRight: '10px' }} /> Fiche Santé</h1>
                    </div>
                    <nav id="sidebar-nav">
                        <ul>
                            <li><span className="active" onClick={() => navigate('/dashboard')}><FaTable /><span>Tableau de bord</span></span></li>
                            <li><span onClick={() => navigate('/organization')}><FaBuilding /><span>Mon organisation</span></span></li>
                        </ul>
                    </nav>
                </aside>

                {sidebarOpen && <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)}></div>}

                <main id="main-content">
                    <header id="header">
                        <button type="button" className="sidebar-toggle" aria-label="Menu" aria-expanded={sidebarOpen} onClick={() => setSidebarOpen(!sidebarOpen)}>
                            ☰
                        </button>
                        <div>
                            <h2>Détails du groupe</h2>
                        </div>
                        <div>
                            <button onClick={() => { AuthService.logout(); navigate('/signin'); }} className="logout-button" aria-label="Déconnexion">
                                <FaArrowRightFromBracket />
                                <span>Déconnexion</span>
                            </button>
                        </div>
                    </header>

                    <section id="edit-unit-section">
                        <div id="edit-unit-container">
                            <div id="unit-details-card">
                                <h3>Détails du groupe : {unit?.name}</h3>

                                <form id="unit-form">
                                    <div>
                                        <label htmlFor="unit-name">Nom :</label>
                                        <input type="text" id="unit-name" value={name} onChange={handleNameChange} />
                                    </div>

                                    <div>
                                        <label htmlFor="unit-type">Type :</label>
                                        <div className="select-wrapper">
                                            <select id="unit-type" value={type} onChange={handleTypeChange}>
                                                {Object.entries(unitTypeLabels).map(([value, label]) => (
                                                    value && <option key={value} value={value}>{label}</option>
                                                ))}
                                            </select>
                                            <FaChevronDown />
                                        </div>
                                    </div>

                                    <div className="form-grid">
                                        <div>
                                            <label htmlFor="start-date">Date de début :</label>
                                            <div className="date-wrapper">
                                                <input type="date" id="start-date" name="start_dt" value={start_dt} onChange={handleDateChange} max={maxStartDate} />
                                                <FaCalendar />
                                            </div>
                                        </div>
                                        <div>
                                            <label htmlFor="end-date">Date de fin :</label>
                                            <div className="date-wrapper">
                                                <input type="date" id="end-date" name="end_dt" value={end_dt} onChange={handleDateChange} min={start_dt} max={maxEndDate} disabled={!start_dt} />
                                                <FaCalendar />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="form-buttons">
                                        <button type="button" onClick={handleSave} className="btn-primary">Sauvegarder</button>
                                        <button type="button" onClick={handleDelete} className="btn-danger">Supprimer</button>
                                    </div>
                                </form>

                                <div role="tablist" className="unit-tabs">
                                    {TABS.map(([key, label]) => (
                                        <button key={key} type="button" role="tab" id={`tab-${key}`} aria-selected={tab === key} aria-controls={`panel-${key}`}
                                            className={tab === key ? 'active' : ''} onClick={() => handleTabChange(key)}>
                                            {label}
                                        </button>
                                    ))}
                                </div>

                                <div id="access-section">
                                    <div role="tabpanel" id="panel-animator" aria-labelledby="tab-animator" hidden={tab !== 'animator'}>
                                        <h4>QR de démarrage (AS / direction)</h4>
                                        {hasAnimators ? (
                                            <p className="tab-empty">Le séjour est démarré. Les autres membres de l'équipe (AS, équipe d'animation) sont invités depuis l'app par l'AS ou la direction, qui choisit leur accès : équipe sanitaire (fiches complètes) ou animation (consignes seulement).</p>
                                        ) : tab === 'animator' && !startup ? (
                                            <div id="animator-link-section">
                                                <p>Le premier membre à rejoindre le séjour en détient l'accès complet et invite ensuite le reste de l'équipe. Ce doit être l'AS ou la direction.</p>
                                                <p>Le QR de démarrage ne sert qu'une fois et reste valable 48 h. En créer un nouveau rend l'ancien inutilisable.</p>
                                                <button type="button" className="btn-primary" onClick={handleCreateStartup}>Créer le QR de démarrage</button>
                                                {startupError && <p className="copy-error" role="alert">{startupError}</p>}
                                            </div>
                                        ) : tab === 'animator' && (
                                            <>
                                                <div id="animator-link-section">
                                                    <p>À scanner <strong>en premier</strong>, avec l'app, par l'AS ou la direction. Ne le transmettez pas à l'équipe d'animation. Valable une fois, jusqu'au {new Date(startup.expires_at).toLocaleString('fr-FR')}.</p>
                                                    <div className="link-container">
                                                        <span>{shareableLink}</span>
                                                        <button type="button" onClick={handleCopyLink} aria-label="Copier le lien" title="Copier le lien"><FaCopy /></button>
                                                    </div>
                                                    {copySuccess && <p className="copy-success">{copySuccess}</p>}
                                                </div>
                                                <div id="qr-code-section">
                                                    <div ref={qrCodeRef}>
                                                        <QRCodeCanvas value={shareableLink} size={192} marginSize={1} />
                                                    </div>
                                                    <button onClick={() => handleDownloadQRCode(qrCodeRef, `qr_code_unite_animateur_${id}.png`)}>
                                                        Télécharger le QR Code
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    <div role="tabpanel" id="panel-parents" aria-labelledby="tab-parents" hidden={tab !== 'parents'}>
                                        <h4>Lien d'invitation pour les parents</h4>
                                        {!hasAnimators ? (
                                            <p className="tab-empty">Il faut d'abord que l'AS ou la direction rejoigne ce séjour.</p>
                                        ) : tab === 'parents' && (
                                            <>
                                                <div id="parent-link-section">
                                                    <p>Voici le lien que vous pouvez envoyer aux parents :</p>
                                                    <div className="link-container">
                                                        <span>{parentShareableLink}</span>
                                                        <button type="button" onClick={handleCopyParentLink} aria-label="Copier le lien" title="Copier le lien"><FaCopy /></button>
                                                    </div>
                                                    {parentCopySuccess && <p className="copy-success">{parentCopySuccess}</p>}
                                                </div>
                                                <div id="qr-code-section">
                                                    <div ref={parentQrCodeRef}>
                                                        <QRCodeCanvas value={parentShareableLink} size={192} marginSize={1} />
                                                    </div>
                                                    <button onClick={() => handleDownloadQRCode(parentQrCodeRef, `qr_code_unite_parent_${id}.png`)}>
                                                        Télécharger le QR Code
                                                    </button>
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    {/* Toujours monté : évite de perdre le tableau en cours de saisie en changeant d'onglet */}
                                    <div role="tabpanel" id="panel-expected" aria-labelledby="tab-expected" hidden={tab !== 'expected'}>
                                        <div className="tab-explain">
                                            <h4>Liste des participants attendus</h4>
                                            <p><strong>À quoi ça sert ?</strong> Vous indiquez ici les enfants inscrits au séjour. L'équipe voit alors, dans l'app, qui a déjà rempli sa fiche santé et qui ne l'a pas encore fait.</p>
                                            <p><strong>Est-ce sécurisé ?</strong> Oui. La liste est chiffrée sur votre ordinateur avant d'être envoyée : notre serveur ne peut pas la lire. Seule l'équipe du séjour, grâce à l'app, peut la déchiffrer.</p>
                                            <p><strong>Pourquoi ne la retrouve-t-on pas depuis un autre ordinateur ?</strong> Justement parce que notre serveur ne peut pas la lire : une copie reste uniquement dans le navigateur de l'ordinateur utilisé pour l'envoi. Depuis un autre ordinateur, vous ne la verrez pas. Vous pourrez toujours en envoyer une nouvelle, qui remplacera entièrement l'ancienne.</p>
                                            <p><strong>Et l'adresse mail d'un parent ?</strong> Elle est facultative. Si vous la renseignez, l'équipe pourra, d'un seul bouton dans l'app, envoyer automatiquement un mail de relance aux parents dont la fiche n'est pas encore remplie. Sans adresse, il faudra les relancer vous-même.</p>
                                        </div>
                                        <ExpectedChildren unit={unit} receivedCount={Number(unit.profiles ?? 0)} />
                                    </div>

                                    <div role="tabpanel" id="panel-devices" aria-labelledby="tab-devices" hidden={tab !== 'devices'}>
                                        <h4>Membres de l'équipe</h4>
                                        {devices.length === 0 ? (
                                            <p>Personne n'a encore rejoint ce groupe / séjour.</p>
                                        ) : (
                                            <ul className="devices-list">
                                                {devices.map((device) => (
                                                    <li key={device.id}>
                                                        <span>
                                                            <span className="device-name">{device.name}</span>
                                                            <span className="device-meta">{device.brand} · {accessLabel(device.access)}</span>
                                                        </span>
                                                        <button type="button" onClick={() => handleDeleteDevice(device.id)} className="delete-device-btn" aria-label={`Retirer ${device.name}`} title="Retirer ce membre">
                                                            <FaTrash />
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </main>
            </div>
        </div>
    );
};

export default UnitDetail;